import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

export interface SeparatorProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  orientation?: "horizontal" | "vertical";
  /** Libellé centré sur le trait (« ou », « Archivés »…). Horizontal uniquement. */
  label?: ReactNode;
}

/**
 * Trait de séparation.
 *
 * Sans libellé c'est une décoration : `role="none"` évite au lecteur d'écran
 * d'annoncer un séparateur qui n'apporte aucune information. Avec un libellé,
 * on garde le rôle sémantique.
 */
export function Separator({
  className,
  orientation = "horizontal",
  label,
  ...props
}: SeparatorProps) {
  if (label) {
    return (
      <div className={cn("flex items-center gap-3", className)} {...props}>
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium tracking-wide text-fg-muted uppercase">{label}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    );
  }

  return (
    <div
      role="separator"
      aria-orientation={orientation}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-px w-full" : "h-full w-px self-stretch",
        className,
      )}
      {...props}
    />
  );
}
